import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

const RoleRequestTable = () => {
  const [roleRequests, setRoleRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const usersPerPage = 7;

  // Fetch current user email from API
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }

        const userData = await response.json();
        if (userData && userData.email) {
          setEmail(userData.email);
        } else {
          console.error('No email found in user data');
          toast.error('Unable to fetch user information');
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        toast.error('Failed to load user information');
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  // Fetch role requests when email is available
  useEffect(() => {
    if (!email) return;

    const fetchRoleRequests = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${AQ_BASE_URL}/aq/api/getRoleRequests?email=${encodeURIComponent(email)}`,
          {
            method: 'GET',
            headers: { accept: 'application/json' },
            credentials: 'include',
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch permission requests');
        }

        const data = await response.json();

        if (data.success) {
          const formattedData = data.role_requests.map((request) => ({
            id: request.id,
            firstname: request.first_name,
            lastname: request.last_name,
            email: request.user_email,
            requested_role: request.requested_role,
            reason: request.reason || 'No reason provided',
            request_date: new Date(request.timestamp).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }),
            status: request.status,
            requested_permission_id: request.requested_permission_id,
            permission_name: request.permission_name,
            permission_description: request.permission_description,
            admin_comment: request.admin_comment,
          }));
          setRoleRequests(formattedData);
        }
      } catch (error) {
        console.error('Error fetching permission requests:', error);
        toast.error('Failed to load permission requests');
      } finally {
        setLoading(false);
      }
    };

    fetchRoleRequests();

    // Refresh every 30 seconds to get real-time updates
    const interval = setInterval(() => {
      fetchRoleRequests();
    }, 900000);

    return () => clearInterval(interval);
  }, [email]);

  const handleAccept = async (requestId, permissionName, userName) => {
    if (!email) {
      toast.error('User email not available');
      return;
    }

    try {
      setRoleRequests((prev) =>
        prev.map((request) =>
          request.id === requestId ? { ...request, status: 'processing' } : request
        )
      );

      toast.info(`Processing permission approval for ${userName}...`);

      const response = await fetch(
        `${AQ_BASE_URL}/aq/api/roleAccept?role_request_id=${requestId}&email=${encodeURIComponent(email)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            admin_comment: 'Permission granted by admin',
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setRoleRequests((prev) =>
          prev.map((request) =>
            request.id === requestId ? { ...request, status: 'approved' } : request
          )
        );

        toast.success(
          <div>
            <div className="font-bold">Permission Approved!</div>
            <div>{data.message}</div>
            <div className="text-sm mt-1">
              {permissionName} → {userName}
            </div>
          </div>,
          {
            autoClose: 5000,
            closeButton: true,
          }
        );

        console.log(`Permission request ID: ${requestId} has been approved`);
      } else {
        toast.error(`Failed to approve: ${data.message || 'Unknown error'}`);
        setRoleRequests((prev) =>
          prev.map((request) =>
            request.id === requestId ? { ...request, status: 'pending' } : request
          )
        );
      }
    } catch (error) {
      console.error('Error in API request:', error);
      toast.error(`Error approving permission: ${error.message}`);
      setRoleRequests((prev) =>
        prev.map((request) =>
          request.id === requestId ? { ...request, status: 'pending' } : request
        )
      );
    }
  };

  const handleReject = async (requestId, permissionName, userName) => {
    if (!email) {
      toast.error('User email not available');
      return;
    }

    try {
      setRoleRequests((prev) =>
        prev.map((request) =>
          request.id === requestId ? { ...request, status: 'processing' } : request
        )
      );

      toast.info(`Processing permission rejection for ${userName}...`);

      const response = await fetch(
        `${AQ_BASE_URL}/aq/api/roleReject?role_request_id=${requestId}&email=${encodeURIComponent(email)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            admin_comment: 'Permission denied by admin',
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setRoleRequests((prev) =>
          prev.map((request) =>
            request.id === requestId ? { ...request, status: 'rejected' } : request
          )
        );

        toast.warning(
          <div>
            <div className="font-bold">Permission Rejected!</div>
            <div>{data.message}</div>
            <div className="text-sm mt-1">
              {permissionName} → {userName}
            </div>
          </div>,
          {
            autoClose: 5000,
            closeButton: true,
          }
        );

        console.log(`Permission request ID: ${requestId} has been rejected`);
      } else {
        toast.error(`Failed to reject: ${data.message || 'Unknown error'}`);
        setRoleRequests((prev) =>
          prev.map((request) =>
            request.id === requestId ? { ...request, status: 'pending' } : request
          )
        );
      }
    } catch (error) {
      console.error('Error in API request:', error);
      toast.error(`Error rejecting permission: ${error.message}`);
      setRoleRequests((prev) =>
        prev.map((request) =>
          request.id === requestId ? { ...request, status: 'pending' } : request
        )
      );
    }
  };

  const totalPages = Math.ceil(roleRequests.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const currentRequests = roleRequests.slice(startIndex, endIndex);

  const handlePageClick = (page) => setCurrentPage(page);

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageClick(i)}
          className={`min-w-[40px] h-10 px-3 rounded-lg mx-1 transition-all ${
            currentPage === i
              ? 'bg-black text-white font-semibold'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  // Helper function to truncate long text
  const truncateText = (text, maxLength = 30) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderRoleRequestTable = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center p-8">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mb-2"></div>
            <div className="text-lg">Loading permission requests...</div>
          </div>
        </div>
      );
    }

    if (!email) {
      return (
        <div className="flex justify-center items-center p-8">
          <div className="text-lg text-red-500">Unable to fetch user information</div>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto rounded-lg h-screen">
        <div className="bg-white py-4 border-b">
          <h1 className="text-xl font-bold text-gray-800">Permission Access Requests</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage and review permission requests from users
          </p>
        </div>

        <div className="bg-white">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-3 px-3 text-left text-xs font-medium text-gray-800 uppercase tracking-wider">
                  <div className="flex items-center">
                    <span>User</span>
                  </div>
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-800 uppercase tracking-wider">
                  <div className="flex items-center">
                    <span>Current Role</span>
                  </div>
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-800 uppercase tracking-wider">
                  <div className="flex items-center">
                    <span>Permission Requested</span>
                  </div>
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-800 uppercase tracking-wider">
                  <div className="flex items-center">
                    <span>Reason</span>
                  </div>
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-800 uppercase tracking-wider">
                  <div className="flex items-center">
                    <span>Request Date</span>
                  </div>
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-800 uppercase tracking-wider">
                  <div className="flex items-center">
                    <span>Status</span>
                  </div>
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-800 uppercase tracking-wider">
                  <div className="flex items-center">
                    <span>Action</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentRequests.length > 0 ? (
                currentRequests.map((request, id) => (
                  <tr
                    key={id}
                    className={`hover:bg-gray-50 transition-colors ${
                      request.status === 'processing' ? 'animate-pulse bg-blue-50' : ''
                    }`}
                  >
                    <td className="py-4 px-3">
                      <div className="flex flex-col">
                        <div className="font-medium text-gray-900">
                          {`${request.firstname} ${request.lastname}`}
                        </div>
                        <div
                          className="text-sm text-gray-500 truncate max-w-[200px]"
                          title={request.email}
                        >
                          {request.email}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="max-w-[150px]">
                        <span
                          className="inline-block px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium truncate max-w-full"
                          title={request.requested_role}
                        >
                          {truncateText(request.requested_role, 20)}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="max-w-[220px]">
                        {request.permission_name ? (
                          <div className="space-y-1">
                            <div
                              className="font-medium text-blue-600 truncate"
                              title={request.permission_name}
                            >
                              {truncateText(request.permission_name, 25)}
                            </div>
                            {request.permission_description && (
                              <div
                                className="text-xs text-gray-500 truncate"
                                title={request.permission_description}
                              >
                                {truncateText(request.permission_description, 35)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">No permission requested</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="max-w-[220px]">
                        <div
                          className="text-sm text-gray-700 truncate-multiline line-clamp-2"
                          title={request.reason}
                        >
                          {request.reason}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm text-gray-600 whitespace-nowrap">
                        {request.request_date}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="space-y-1">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            request.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : request.status === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : request.status === 'rejected'
                                  ? 'bg-red-100 text-red-800'
                                  : request.status === 'processing'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {request.status === 'processing' && (
                            <svg
                              className="animate-spin -ml-1 mr-1 h-3 w-3 text-blue-800"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          )}
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                        {request.admin_comment && (
                          <div
                            className="text-xs text-gray-500 truncate max-w-[150px]"
                            title={`Admin Comment: ${request.admin_comment}`}
                          >
                            {truncateText(request.admin_comment, 25)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {request.status === 'pending' ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() =>
                              handleAccept(
                                request.id,
                                request.permission_name,
                                `${request.firstname} ${request.lastname}`
                              )
                            }
                            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium text-sm transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={request.status === 'processing'}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() =>
                              handleReject(
                                request.id,
                                request.permission_name,
                                `${request.firstname} ${request.lastname}`
                              )
                            }
                            className="px-4 py-2 bg-white border border-red-500 text-red-500 rounded-lg hover:bg-red-50 font-medium text-sm transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={request.status === 'processing'}
                          >
                            Reject
                          </button>
                        </div>
                      ) : request.status === 'processing' ? (
                        <div className="flex items-center text-blue-600">
                          <svg
                            className="animate-spin h-4 w-4 mr-2"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span className="text-sm">Processing...</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Action completed</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="py-12 px-4 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          ></path>
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-1">
                        No permission requests
                      </h3>
                      <p className="text-gray-500">
                        When users request page access, they will appear here.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <div className="space-y-6">
        {renderRoleRequestTable()}

        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-white border-t border-gray-200 rounded-lg">
            <div className="text-sm text-gray-700 mb-3 sm:mb-0">
              Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
              <span className="font-medium">{Math.min(endIndex, roleRequests.length)}</span> of{' '}
              <span className="font-medium">{roleRequests.length}</span> requests
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex items-center space-x-1">{generatePageNumbers()}</div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RoleRequestTable;
